/**
 * Revision history of a single rule.
 */

import { createPortal } from 'react-dom';
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { useRuleHistory } from '../../../hooks/queries/useRules';
import { FinanceRule } from '../../../services/api/ruleService';
import { ClassificationBadge } from '../contract-workflow/shared/PlaybookReviewPanel';
import { contractTypeLabel, personName } from './ruleBookConstants';

const CHANGE_LABEL = {
  created: 'Created',
  updated: 'Edited',
  enabled: 'Turned on',
  disabled: 'Turned off',
  deleted: 'Deleted'
} as const;

export const RuleHistoryDrawer: React.FC<{ rule: FinanceRule; onClose: () => void }> = ({ rule, onClose }) => {
  const { data: revisions = [], isLoading, error } = useRuleHistory(rule._id);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portalled to <body>: rendered in place, the overlay picks up layout from its
  // parent (e.g. a space-y margin) and stops covering the whole window.
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="rule-history-title"
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md h-full flex flex-col bg-white dark:bg-dark-surface shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-dark-border">
          <div className="min-w-0">
            <h2 id="rule-history-title" className="text-base font-semibold text-gray-900 dark:text-white">History</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{rule.title}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading && <p className="text-sm text-gray-500">Loading history...</p>}
          {error && <p className="text-sm text-red-600">Couldn't load history: {(error as Error).message}</p>}

          <ol className="space-y-4">
            {revisions.map(rev => (
              <li key={rev._id} className="relative pl-5 border-l-2 border-gray-200 dark:border-dark-border">
                <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-primary-500 dark:bg-accent-400" />
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{CHANGE_LABEL[rev.change]}</span>
                  <span className="text-xs text-gray-400">v{rev.version}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {personName(rev.changedBy)} &middot; {new Date(rev.changedAt).toLocaleString()}
                </p>
                {(rev.change === 'created' || rev.change === 'updated') && (
                  <div className="mt-2 rounded-lg bg-gray-50 dark:bg-dark-bg-secondary p-3 text-sm space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-white">{rev.snapshot.title}</span>
                      <ClassificationBadge classification={rev.snapshot.severity} />
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">{rev.snapshot.ruleText}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {rev.snapshot.category} &middot;{' '}
                      {rev.snapshot.appliesTo?.length
                        ? rev.snapshot.appliesTo.map(contractTypeLabel).join(', ')
                        : 'All contract types'}
                    </p>
                    {rev.snapshot.suggestedPosition && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">Position: {rev.snapshot.suggestedPosition}</p>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default RuleHistoryDrawer;
